import type { InternalAxiosRequestConfig } from "axios";

import {
  type ApiClientName,
  logAxiosSessionExpiration
} from "@repo/core/services/observability/requestErrorLogger";
import { cancelPreProposalAlterarSyncDebounce } from "@repo/core/screens/PreProposal/services/preProposalAlterarSyncDebounce.service";
import useAuthStore from "@repo/core/stores/auth";
import { useStoresAndShopkeepersStore } from "@repo/core/stores/storesAndShopkeepers";
import {
  getSessionEpoch,
  isCurrentSessionEpoch,
  isSessionInvalidated,
  SessionExpiredFlowError
} from "./sessionEpoch";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    _appSessionEpoch?: number;
  }
}

type SessionSnapshot = {
  isAuthenticated: boolean;
  user: { token?: string } | null;
};

type SessionExpirationContext = {
  apiClient: ApiClientName;
  error: unknown;
};

export const hasActiveSession = (session: SessionSnapshot): boolean =>
  session.isAuthenticated || Boolean(session.user?.token);

export const isUnauthorizedResponse = (status: unknown, data: unknown) => {
  const responseData = data as
    | { error?: { Code?: number | string; code?: number | string } }
    | undefined;
  const errorCode = responseData?.error?.Code ?? responseData?.error?.code;

  return Number(status) === 401 || Number(errorCode) === 401;
};

export const expireAuthenticatedSession = (
  context?: SessionExpirationContext
): boolean => {
  const session = useAuthStore.getState();

  if (!hasActiveSession(session)) {
    return false;
  }

  if (context) {
    logAxiosSessionExpiration(context.apiClient, context.error);
  }

  cancelPreProposalAlterarSyncDebounce();
  session.logout();
  useStoresAndShopkeepersStore.getState().setShowCorrectStoreAlert(true);
  return true;
};

export const canContinueAuthenticatedFlow = (): boolean =>
  !isSessionInvalidated() && hasActiveSession(useAuthStore.getState());

export const continueAuthenticatedFlow = (action: () => void): boolean => {
  if (!canContinueAuthenticatedFlow()) {
    return false;
  }

  action();
  return true;
};

export const startAuthenticatedRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (isSessionInvalidated()) {
    throw new SessionExpiredFlowError();
  }

  config._appSessionEpoch = getSessionEpoch();
  return config;
};

export const isCurrentSessionRequest = (
  config?: InternalAxiosRequestConfig
): boolean =>
  config?._appSessionEpoch === undefined ||
  isCurrentSessionEpoch(config._appSessionEpoch);

export const rejectStaleSessionResponse = (
  config?: InternalAxiosRequestConfig
): void => {
  if (!isCurrentSessionRequest(config)) {
    throw new SessionExpiredFlowError();
  }
};
